import React, { useEffect, useRef, useState } from 'react';
import {
  useInView,
  useReducedMotion,
  animate,
} from 'motion/react';

/**
 * <AnimatedCounter> — counts from 0 → value when scrolled into view.
 * Used for hero metrics ("8-Stage", "100%"), trust stats, etc.
 *
 * Props:
 *  value     — numeric end value (default 0)
 *  prefix    — string prefix (e.g. "₹", default '')
 *  suffix    — string suffix (e.g. "%", "+", default '')
 *  decimals  — number of decimal places (default 0)
 *  duration  — seconds (default 1.6)
 *  className — passthrough
 */
const AnimatedCounter = ({
  value = 0,
  prefix = '',
  suffix = '',
  decimals = 0,
  duration = 1.6,
  className = '',
}) => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [display, setDisplay] = useState(reduce ? value : 0);
  const started = useRef(false);

  useEffect(() => {
    if (!inView || started.current) return;
    if (reduce) {
      setDisplay(value);
      started.current = true;
      return;
    }
    started.current = true;
    const controls = animate(0, value, {
      duration,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(v),
    });
    return () => controls.stop();
  }, [inView, value, duration, reduce]);

  const formatted = Number(display).toLocaleString('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return (
    <span ref={ref} className={`jmi-counter ${className}`}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
};

export default AnimatedCounter;
